import { useState, ReactElement, useEffect, useMemo, useCallback } from 'react';
import { v4 as uuid } from 'uuid';
import { IdentityProvider, ProductFeature, PatchOrganizationSettingsRequest } from '@localstack/types';
import { ExternalLink, SUB_ACTIVE_STATUSES } from '@localstack/constants';
import { useForm } from 'react-hook-form';
import { ProgressButton, LoadingFragment, ButtonWithDialog } from '@localstack/ui';
import {
  useApiGetter,
  useApiEffect,
  OrganizationsService,
  ScimTokensService,
  OrganizationService,
} from '@localstack/services';
import { AddCircle as AddIcon, OpenInNew as OpenInNewIcon, DeleteOutlined as DeleteIcon } from '@mui/icons-material';
import { Typography, Box, Card, CardHeader, CardContent, Grid, CardActions, Button } from '@mui/material';
import { red } from '@mui/material/colors';

import { useAuthProvider } from '~/hooks/useAuthProvider';
import { useAggregatedSubscriptionInfo } from '~/hooks/useSubscriptions';

import { SCIMSettingsDialog } from './SCIMSettingsDialog';
import { IdpList } from './IdpList';
import { IdpForm } from './IdpForm';
import { HeaderActions, LearnMoreAnchor, FullWidthGridItem, SkeletonPaper, MemberCard, HeaderTight } from './styled';
import { buildSubscriptionGroups, cleanAttributeMapping, blobToBase64, toMetadataBlob } from './utils';
import { IdpFormData, IDP_SCIM_SKELETON_HEIGHT, IDP_SKELETON_HEIGHT, MetadataType } from './types';

export const SSOSettings = (): ReactElement => {
  const { hasFeature } = useAuthProvider();
  const hasScim = hasFeature(ProductFeature.FEATURE_SCIM);

  const { data: subscriptionsData, isLoading: isLoadingSubscriptions } = useAggregatedSubscriptionInfo();
  const { subscriptions } = subscriptionsData || {};

  const { data: organization } = useApiGetter(OrganizationService, 'getOrganization', []);

  const organizationId = organization?.id;

  const { data: identityProviders, isLoading: isIdpLoading } = useApiGetter(
    OrganizationsService,
    'listIdentityProviders',
    [organizationId],
    { enable: !!organizationId, defaultValue: [] },
  );

  const { data: scimTokens, isLoading: isListScimTokensLoading } = useApiGetter(
    ScimTokensService,
    'listScimTokens',
    [],
    { enable: hasScim },
  );

  const {
    storeIdentityProvider,
    deleteIdentityProvider,
    isLoading: isIdpUpdating,
  } = useApiEffect(OrganizationsService, ['storeIdentityProvider', 'deleteIdentityProvider'], {
    revalidate: ['listIdentityProviders'],
    revalidateOtherClient: {
      client: [ScimTokensService, OrganizationService],
      methods: ['listScimTokens', 'getOrganization'],
    },
  });

  const { rotateScimToken, isLoading: isScimTokenUpdating } = useApiEffect(ScimTokensService, ['rotateScimToken'], {
    revalidate: ['listScimTokens'],
  });

  const { patchOrganizationSettings, isLoading: isOrgUpdating } = useApiEffect(
    OrganizationService,
    ['patchOrganizationSettings'],
    {
      revalidate: ['getOrganization'],
      // listScimTokens lazily creates tokens for all configured IdPs,
      // so revalidating it is what mints the token for a new provider
      revalidateOtherClient: { client: [ScimTokensService], methods: ['listScimTokens'] },
    },
  );

  const isLoading = isIdpLoading || isIdpUpdating || isOrgUpdating || (hasScim && isLoadingSubscriptions);

  const { control, handleSubmit, formState, reset, setValue, getValues, unregister } = useForm<IdpFormData>({
    mode: 'all',
  });

  const [addNewProvider, setAddNewProvider] = useState(false);
  const [metadataType, setMetadataType] = useState<MetadataType>('MetadataURL');
  const [selectedProvider, setSelectedProvider] = useState<Optional<IdentityProvider>>(null);
  const [viewingScimIdpName, setViewingScimIdpName] = useState<string>('');
  const [isScimConfigOpen, setIsScimConfigOpen] = useState(false);
  const [scimToggleLoading, setScimToggleLoading] = useState<{ [idpName: string]: boolean }>({});

  const isAnyScimToggleLoading = Object.values(scimToggleLoading).some(Boolean);

  const handleToggleScim = async (name: string, checked: boolean) => {
    if (!hasScim) return;
    setScimToggleLoading((prev) => ({ ...prev, [name]: true }));

    try {
      const scimSettings = { ...(organization?.scim_settings ?? {}) };

      if (checked) {
        Object.keys(scimSettings).forEach((scimSettingsIdpName) => {
          scimSettings[scimSettingsIdpName] = {
            ...scimSettings[scimSettingsIdpName],
            is_enabled: scimSettingsIdpName === name,
          };
        });
        if (!scimSettings[name]) scimSettings[name] = { is_enabled: true };
      } else {
        scimSettings[name] = { ...scimSettings[name], is_enabled: false };
      }

      await patchOrganizationSettings({ scim_settings: scimSettings });
    } finally {
      setScimToggleLoading((prev) => ({ ...prev, [name]: false }));
    }
  };

  const existingIdpNames = useMemo(
    () => new Set((identityProviders ?? []).map((p) => p.idp_name)),
    [identityProviders],
  );

  const isAnotherScimEnabled = useMemo(() => {
    if (!hasScim) return false;
    const scim = organization?.scim_settings ?? {};
    return Object.entries(scim).some(([name, v]) => v?.is_enabled && existingIdpNames.has(name));
  }, [hasScim, organization?.scim_settings, existingIdpNames]);

  const onSubmit = async (data: IdpFormData) => {
    const { idp, sso_settings } = data;

    // Cognito takes one metadata source, so only the selected one is sent. Both stay in the form,
    // so switching the type to look at the other loses neither.
    const isSaml = idp.provider_type === 'SAML';
    const metadata = idp.MetadataFile;
    const cleanedIdp: IdentityProvider = {
      ...idp,
      // a File after an upload, the XML Cognito returned otherwise - reading the latter as a
      // File is what made saving throw
      MetadataFile:
        isSaml && metadataType === 'MetadataFile' && metadata
          ? await blobToBase64(toMetadataBlob(metadata))
          : undefined,
      MetadataURL: isSaml && metadataType === 'MetadataURL' ? idp.MetadataURL : undefined,
      attribute_mapping: cleanAttributeMapping(idp.attribute_mapping),
    };

    const updatedSettings: PatchOrganizationSettingsRequest = {
      sso_settings: { ...organization?.sso_settings, [idp.idp_name]: sso_settings },
    };

    // useApiEffect swallows errors and resolves to undefined; abort so we don't
    // write settings or create a SCIM token for an IdP that was never stored
    const storedIdp = await storeIdentityProvider(organizationId, idp.idp_name, cleanedIdp);
    if (!storedIdp) return;

    const updatedOrg = await patchOrganizationSettings(updatedSettings);
    if (!updatedOrg) return;

    setAddNewProvider(false);
    setSelectedProvider(null);
  };

  const onAddNewProvider = () => {
    setAddNewProvider(true);
    setSelectedProvider(null);
    reset();
  };

  const onSelectProvider = (idp: IdentityProvider) => {
    if (idp.idp_name === selectedProvider?.idp_name) return;
    setSelectedProvider(idp);
    setAddNewProvider(false);
  };

  useEffect(() => {
    setMetadataType(selectedProvider?.MetadataFile ? 'MetadataFile' : 'MetadataURL');
  }, [selectedProvider]);

  const onMetadataTypeChange = (nextMetadataType: MetadataType) => {
    const leftBehind = nextMetadataType === 'MetadataURL' ? 'idp.MetadataFile' : 'idp.MetadataURL';
    const keptValue = getValues(leftBehind);

    setMetadataType(nextMetadataType);
    // an off-screen field is still validated, so unregister the one left behind - only its
    // registry entry has to go, hence the value straight back
    unregister(leftBehind);
    setValue(leftBehind, keptValue);
  };

  useEffect(() => {
    if (!selectedProvider) return reset();

    Object.keys(selectedProvider).forEach((field) => {
      const castedField = field as keyof IdentityProvider;
      setValue(`idp.${castedField}` as keyof IdpFormData, selectedProvider?.[castedField] as never, {
        shouldValidate: true,
      });
    });

    if (!organization) return;

    const ssoSettings = organization?.sso_settings?.[selectedProvider.idp_name];
    Object.entries(ssoSettings ?? {}).forEach(([field, value]) => {
      setValue(`sso_settings.${field}` as keyof IdpFormData, value, { shouldValidate: true });
    });
  }, [selectedProvider]);

  const subscriptionGroups = useMemo(() => {
    if (!hasScim) return undefined;
    const active = (subscriptions ?? []).filter(({ status }) => SUB_ACTIVE_STATUSES.includes(status));
    return buildSubscriptionGroups(active);
  }, [hasScim, subscriptions]);

  const onDeleteProvider = useCallback(async () => {
    if (!selectedProvider || !organizationId) return;

    // the backend deletes the provider's whole footprint with it:
    // sso_settings, scim_settings and the SCIM token
    const deletedIdp = await deleteIdentityProvider(organizationId, selectedProvider.idp_name);
    if (!deletedIdp) return;

    setSelectedProvider(null);
  }, [selectedProvider, organizationId, deleteIdentityProvider]);

  const showScimSection = hasScim && !!subscriptionGroups;

  return (
    <Box>
      {hasScim && (
        <SCIMSettingsDialog
          isOpen={isScimConfigOpen}
          onClose={() => setIsScimConfigOpen(false)}
          onTokenRotate={rotateScimToken}
          isLoading={isScimTokenUpdating || isListScimTokensLoading}
          isLoadingSubscriptions={isLoadingSubscriptions}
          idpName={viewingScimIdpName}
          subscriptionGroups={subscriptionGroups}
          token={scimTokens?.[viewingScimIdpName]?.token?.scim_token}
          baseUrl={scimTokens?.[viewingScimIdpName]?.base_url}
        />
      )}

      <Card>
        <CardHeader
          title="Single Sign-on Identity Providers"
          action={
            <HeaderActions>
              <Button color="primary" disabled={addNewProvider} onClick={onAddNewProvider} startIcon={<AddIcon />}>
                Add New Identity Provider
              </Button>
            </HeaderActions>
          }
        />
        <CardContent sx={{ pt: 0, px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
          <Grid container spacing={{ xs: 2, md: 3 }} sx={{ width: '100%' }}>
            <Grid item xs={12} md={12} lg={4}>
              <Grid container spacing={0} sx={{ width: '100%' }}>
                <Typography variant="body1">
                  Manage authentication via SSO{hasScim && ' and SCIM'}.{' '}
                  <LearnMoreAnchor href={ExternalLink.DOCS_SSO} target="_blank" rel="noopener noreferrer">
                    Learn more
                    <OpenInNewIcon fontSize="inherit" sx={{ ml: 0.5, fontSize: '0.85rem' }} />
                  </LearnMoreAnchor>
                </Typography>

                {isIdpLoading ? (
                  new Array(1).fill(null).map((_, _index) => (
                    <FullWidthGridItem item xs={12} key={uuid()}>
                      <SkeletonPaper variant="outlined">
                        <LoadingFragment
                          variant="card"
                          height={showScimSection ? IDP_SCIM_SKELETON_HEIGHT : IDP_SKELETON_HEIGHT}
                          loading
                        />
                      </SkeletonPaper>
                    </FullWidthGridItem>
                  ))
                ) : identityProviders?.length === 0 ? (
                  <FullWidthGridItem item xs={12}>
                    <Box>
                      <Typography variant="body1">
                        You have not added any identity providers yet. <br />
                        Click the &quot;Add New Identity Provider&quot; button on the top right-hand side to get
                        started.
                      </Typography>
                    </Box>
                  </FullWidthGridItem>
                ) : (
                  <IdpList
                    identityProviders={identityProviders ?? []}
                    selectedProviderName={selectedProvider?.idp_name}
                    onSelectProvider={onSelectProvider}
                    organization={organization}
                    isIdpLoading={isIdpLoading}
                    isAnotherScimEnabled={isAnotherScimEnabled}
                    isAnyScimToggleLoading={isAnyScimToggleLoading}
                    showScimSection={showScimSection}
                    onOpenScimConfig={(name) => {
                      setViewingScimIdpName(name);
                      setIsScimConfigOpen(true);
                    }}
                    onToggleScim={handleToggleScim}
                    getScimToggleLoading={(name) => !!scimToggleLoading[name]}
                  />
                )}
              </Grid>
            </Grid>

            {(selectedProvider || addNewProvider) && (
              <Grid item xs={12} md={12} lg={8}>
                <form
                  onSubmit={handleSubmit(onSubmit)}
                  style={{ display: 'flex', alignItems: 'center', width: '100%' }}
                >
                  <MemberCard sx={{ mt: { md: 0 }, pt: { md: 0 }, width: '100%' }}>
                    <HeaderTight
                      title={
                        <Typography variant="subtitle1" noWrap>
                          <strong>{addNewProvider ? 'New Identity Provider' : selectedProvider?.idp_name}</strong>
                        </Typography>
                      }
                      subheader={addNewProvider ? '' : selectedProvider?.provider_type}
                      action={
                        !addNewProvider && (
                          <ButtonWithDialog
                            componentType="Button"
                            size="small"
                            startIcon={<DeleteIcon />}
                            sx={{ color: red.A200 }}
                            dialogTitle="Remove Identity Provider"
                            dialogContent={
                              `Are you sure you want to remove the identity provider "${selectedProvider?.idp_name}"? ` +
                              `This will delete its SSO configuration${hasScim ? ' and invalidate its SCIM token' : ''} and cannot be undone.`
                            }
                            confirmButtonText="Remove"
                            confirmButtonProps={{ color: 'error' }}
                            onClick={onDeleteProvider}
                          >
                            Remove identity provider
                          </ButtonWithDialog>
                        )
                      }
                    />
                    <CardContent>
                      <IdpForm
                        control={control}
                        formState={formState}
                        selectedProvider={selectedProvider}
                        organization={organization}
                        metadataType={metadataType}
                        onMetadataTypeChange={onMetadataTypeChange}
                      />
                    </CardContent>

                    <CardActions>
                      <ProgressButton
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={!formState.isValid}
                        loading={isLoading}
                      >
                        Save
                      </ProgressButton>
                    </CardActions>
                  </MemberCard>
                </form>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};
